// CandidateDashboard.js - Fixed pagination context issues
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import Sidebar from '../Sidebar';
import LogoutButton from '../Login/LogoutButton';
import CandidateTable from './CandidateTable';
import PublicApplicationsTable from './PublicApplicationsTable';
import SearchBar from './SearchBar';
import SearchResultsInfo from './SearchResultsInfo';
import FiltersSection from './FiltersSection';
import PaginationControls from './PaginationControls';
import DeleteAllModal from './DeleteAllModal';

// Hooks
import { useDebounce } from './useDebounce';
import { useAuth } from './useAuth';
import { useCandidates } from './useCandidates';

const CandidateDashboard = () => {
    const navigate = useNavigate();
    const { authError, setAuthError, checkAuth } = useAuth(navigate);
    const [activeView, setActiveView] = useState('bulk');

    const {
        candidates,
        setCandidates,
        loading,
        searchLoading,
        searchResults,
        setSearchResults,
        totalResults,
        setTotalResults,
        pagination,
        setPagination,
        CANDIDATES_PER_PAGE,
        clearSearch,
        loadCandidates,
        fetchCandidates,
        deleteAllCandidates,
        deleteRejectedCandidates
    } = useCandidates(navigate, setAuthError);

    // Local state
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState({
        shortlisted: true,
        rejected: true,
        waitlist: true
    });

    // Advanced filters state
    const [advancedFilters, setAdvancedFilters] = useState({
        skills: [],
        locations: [],
        companies: [],
        notice_periods: []
    });

    const [processingActions, setProcessingActions] = useState(new Set());
    const [sendingInterviews, setSendingInterviews] = useState(new Set());
    const [deletingCandidates, setDeletingCandidates] = useState(new Set());
    const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
    const [isDeletingAll, setIsDeletingAll] = useState(false);
    const [isDeletingRejected, setIsDeletingRejected] = useState(false);

    // Debounced search term
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Authentication check on mount
    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (token && checkAuth()) {
            loadCandidates(1, '', {});
        }
    }, []);

    // Check if any advanced filters are active
    const hasActiveAdvancedFilters = useMemo(() => {
        return Object.values(advancedFilters).some(filters =>
            Array.isArray(filters) && filters.length > 0
        );
    }, [advancedFilters]);

    // Handle search and filters changes
    useEffect(() => {
        const hasSearch = debouncedSearchTerm.trim().length > 0;
        const hasFilters = hasActiveAdvancedFilters;

        if (hasSearch || hasFilters) {
            fetchCandidates(debouncedSearchTerm, 1, advancedFilters);
        } else {
            setSearchResults(null);
            if (candidates.length === 0) {
                loadCandidates(1, '', {});
            }
        }
    }, [debouncedSearchTerm, advancedFilters, hasActiveAdvancedFilters]);

    // Search handlers
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (!value.trim() && !hasActiveAdvancedFilters) {
            setSearchResults(null);
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setAdvancedFilters({
            skills: [],
            locations: [],
            companies: [],
            notice_periods: []
        });
        setSearchResults(null);
    };

    // Advanced filters handler
    const handleAdvancedFilterChange = (newFilters) => {
        //console.log('🎯 Advanced filters updated:', newFilters);
        setAdvancedFilters(newFilters);
    };

    // FIXED: Pagination handlers now always maintain context
    const handleNextPage = () => {
        if (pagination.hasNextPage) {
            const nextPage = pagination.currentPage + 1;
            // Always pass current search context to maintain consistency
            fetchCandidates(searchTerm, nextPage, advancedFilters);
        }
    };

    const handlePreviousPage = () => {
        if (pagination.hasPreviousPage) {
            const prevPage = pagination.currentPage - 1;
            // Always pass current search context to maintain consistency
            fetchCandidates(searchTerm, prevPage, advancedFilters);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            // Always pass current search context to maintain consistency
            fetchCandidates(searchTerm, page, advancedFilters);
        }
    };

    // Delete all handler
    const handleDeleteAllCandidates = async () => {
        setIsDeletingAll(true);
        const success = await deleteAllCandidates();
        setIsDeletingAll(false);
        setShowDeleteAllConfirm(false);
    };

    // Delete rejected handler
    const handleDeleteRejectedCandidates = async () => {
        setIsDeletingRejected(true);
        const success = await deleteRejectedCandidates();
        setIsDeletingRejected(false);
        setShowDeleteAllConfirm(false);
    };

    // Filter change handler
    const handleFilterChange = (filterType) => {
        setFilter(prev => ({
            ...prev,
            [filterType]: !prev[filterType]
        }));
    };

    // View change handler
    const handleViewChange = (view) => {
        setActiveView(view);
        setSearchTerm('');
        setSearchResults(null);
        // Clear advanced filters when switching views
        setAdvancedFilters({
            skills: [],
            locations: [],
            companies: [],
            notice_periods: []
        });
    };

    // Get candidates to display (either search results or regular candidates)
    const candidatesToDisplay = searchResults !== null ? searchResults : candidates;

    // Filter candidates based on view and status
    // Filter candidates based on view and status
    const filteredCandidates = useMemo(() => {
        let viewFilteredCandidates;

        if (activeView === 'public') {
            // Only show candidates with isPublicApplication: true
            viewFilteredCandidates = candidatesToDisplay.filter(candidate =>
                candidate.isPublicApplication === true
            );
        } else {
            // Only show candidates with isPublicApplication: false or undefined
            viewFilteredCandidates = candidatesToDisplay.filter(candidate =>
                candidate.isPublicApplication !== true
            );
        }

        // Apply status filters - only show processed candidates
        return viewFilteredCandidates.filter(candidate => {
            // Only show candidates that have been processed (have a filterDecision)
            const decision = candidate.filterDecision;
            if (!decision) return false; // Exclude candidates with null/undefined filterDecision

            // If no filters are selected, show nothing
            if (!filter.shortlisted && !filter.rejected && !filter.waitlist) {
                return false;
            }

            // Show candidates based on selected filters
            if (filter.shortlisted && decision === 'ACCEPT') return true;
            if (filter.rejected && decision === 'REJECT') return true;
            if (filter.waitlist && decision === 'UNDER REVIEW') return true;

            return false;
        });
    }, [candidatesToDisplay, activeView, filter]);

    // TEMPORARY: Show all candidates without filtering


    // Empty state message generator
    const getEmptyStateMessage = () => {
        const activeFilters = Object.keys(filter).filter(key => filter[key]);
        const totalCandidates = filteredCandidates.length;
        const isSearchActive = searchResults !== null;
        const viewName = activeView === 'public' ? 'public applications' : 'bulk uploads';
        const hasAdvancedFiltersActive = hasActiveAdvancedFilters;

        if (isSearchActive && (searchTerm.trim() || hasAdvancedFiltersActive)) {
            if (totalCandidates === 0) {
                let searchDescription = '';
                if (searchTerm.trim() && hasAdvancedFiltersActive) {
                    searchDescription = `"${searchTerm}" with applied filters`;
                } else if (searchTerm.trim()) {
                    searchDescription = `"${searchTerm}"`;
                } else if (hasAdvancedFiltersActive) {
                    searchDescription = 'the applied filters';
                }

                return {
                    title: `No ${viewName} found for ${searchDescription}`,
                    subtitle: "Try adjusting your search terms or filters."
                };
            }
        }

        if (totalCandidates === 0 && !isSearchActive) {
            return {
                title: `No ${viewName} yet`,
                subtitle: activeView === 'public'
                    ? "Public applications from your website will appear here."
                    : "Upload resumes to begin candidate screening!"
            };
        }

        if (activeFilters.length === 0) {
            return {
                title: "No filters selected",
                subtitle: "Please select at least one filter to view candidates."
            };
        }

        return {
            title: `No ${viewName} match the selected criteria`,
            subtitle: "Try adjusting your filters or check other pages."
        };
    };

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="text-gray-600 text-xl mb-4">Loading candidates...</div>
                    {authError && (
                        <div className="text-red-600 text-sm">{authError}</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
            {/* Custom scrollbar styles */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 4px;
                    border: 1px solid #f1f5f9;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
                .custom-scrollbar::-webkit-scrollbar-corner {
                    background: #f1f5f9;
                }
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: #cbd5e1 #f1f5f9;
                }
            `}</style>

            {/* Sidebar with view switching */}
            <div className="flex-shrink-0 h-screen overflow-y-auto custom-scrollbar">
                <Sidebar activeView={activeView} onViewChange={handleViewChange} />
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <h1 className="text-lg font-semibold text-gray-900">
                            Hireon AI - {activeView === 'public' ? 'Candidate Applications' : 'Admin Uploads'}
                        </h1>
                        {authError && (
                            <div className="text-red-600 text-sm bg-red-50 px-3 py-1 rounded-md border border-red-200">
                                {authError}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        <SearchBar
                            searchTerm={searchTerm}
                            onSearchChange={handleSearchChange}
                            onClearSearch={handleClearSearch}
                            searchLoading={searchLoading}
                        />
                        <LogoutButton />
                    </div>
                </div>

                {/* Search Results Info */}
                <SearchResultsInfo
                    searchResults={searchResults}
                    searchTerm={searchTerm}
                    searchLoading={searchLoading}
                    totalResults={totalResults}
                    filteredCandidatesLength={filteredCandidates.length}
                    onClearSearch={handleClearSearch}
                    hasAdvancedFilters={hasActiveAdvancedFilters}
                />

                {/* Filters Section */}
                <FiltersSection
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    candidatesPerPage={CANDIDATES_PER_PAGE}
                    candidatesLength={candidates.length}
                    isDeletingAll={isDeletingAll}
                    isDeletingRejected={isDeletingRejected}
                    onDeleteAllClick={() => setShowDeleteAllConfirm(true)}
                    activeView={activeView}
                    onAdvancedFilterChange={handleAdvancedFilterChange}
                    advancedFilters={advancedFilters}
                />

                {/* Conditional Table Rendering */}
                {activeView === 'public' ? (
                    <PublicApplicationsTable
                        candidates={filteredCandidates}
                        navigate={navigate}
                        processingActions={processingActions}
                        setProcessingActions={setProcessingActions}
                        sendingInterviews={sendingInterviews}
                        setSendingInterviews={setSendingInterviews}
                        deletingCandidates={deletingCandidates}
                        setDeletingCandidates={setDeletingCandidates}
                        setCandidates={setCandidates}
                        fetchCandidates={fetchCandidates}
                        setAuthError={setAuthError}
                        authError={authError}
                        getEmptyStateMessage={getEmptyStateMessage}
                        tableType="public"
                        searchTerm={searchTerm}
                        advancedFilters={advancedFilters}
                        currentPage={pagination.currentPage}
                    />
                ) : (
                    <CandidateTable
                        candidates={filteredCandidates}
                        navigate={navigate}
                        processingActions={processingActions}
                        setProcessingActions={setProcessingActions}
                        sendingInterviews={sendingInterviews}
                        setSendingInterviews={setSendingInterviews}
                        deletingCandidates={deletingCandidates}
                        setDeletingCandidates={setDeletingCandidates}
                        setCandidates={setCandidates}
                        fetchCandidates={fetchCandidates}
                        setAuthError={setAuthError}
                        authError={authError}
                        getEmptyStateMessage={getEmptyStateMessage}
                        tableType="default"
                        searchTerm={searchTerm}
                        advancedFilters={advancedFilters}
                        currentPage={pagination.currentPage}
                    />
                )}

                {/* Pagination Controls */}
                <PaginationControls
                    pagination={pagination}
                    onPreviousPage={handlePreviousPage}
                    onNextPage={handleNextPage}
                    onPageChange={handlePageChange}
                />
            </div>

            {/* Delete All Modal */}
            <DeleteAllModal
                show={showDeleteAllConfirm}
                onClose={() => setShowDeleteAllConfirm(false)}
                onConfirm={handleDeleteAllCandidates}
                onDeleteRejected={handleDeleteRejectedCandidates}
                candidatesLength={candidates.length}
                isDeleting={isDeletingAll}
                isDeletingRejected={isDeletingRejected}
            />

            {/* Toast Container */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default CandidateDashboard;